import React, { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import styles from "./index.module.scss";
import Tooltip from "@/components/ToolTip";
import MapView from "@components/CustomComponents/LocatorStore/Map";
import SearchResult from "@components/CustomComponents/LocatorStore/SearchResult";
import { renderRichText } from "@/utils/formatters";
import { MAP_LABELS } from "@/utils/helper";

const getStoreLocationData = locale => {
  const labels = MAP_LABELS[locale] || MAP_LABELS.en;
  return {
    heading: "Find a Store",
    buttonLabel: labels.storelocatorButtonLabel,
    inputField: true,
    checkBoxList: [
      {
        label: labels.centerName,
        name: "KEC",
      },
      {
        label: labels.showroomName,
        name: "Distributor",
      },
      {
        label: labels.dearName,
        name: "authorizedKohlerDealer",
      },
    ],
    showToolTip: true,
    showMap: true,
  };
};

const apiKey = process.env.NEXT_PUBLIC_MAP_API_KEY;
const KohlerCheckBox = ({ name, value, checked, onChange }) => {
  return (
    <div className="cursor-pointer form-options">
      <div className="checkbox-inline flex gap-3">
        <input
          className="rounded border border-gray-300 accent-gray-300 custom-tick cursor-pointer"
          value={value}
          id="koh-exp-center"
          type="checkbox"
          checked={checked}
          onChange={onChange}
        />
        <Tooltip text={name} showArrow={true}>
          <div className="koh-dotted-span">{name}</div>
        </Tooltip>
      </div>
    </div>
  );
};

const StoreLocator = ({ locatorData = {} }) => {
  const { locale } = useRouter();
  const defaultLocale = locale === "default" ? "th" : locale;
  const storeLocationData = getStoreLocationData(defaultLocale);
  const {
    buttonLabel = "",
    checkBoxList = [],
    // showSorryText = true,
  } = storeLocationData;

  const { heading = "", sorryText = "" } = locatorData || {};
  const searchBoxRef = useRef(null);
  const [selected, setSelected] = useState(
    checkBoxList?.map(item => item.name) || []
  );
  const [showSorryText, setShowSorryText] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [location, setLocation] = useState(null);
  const [query, setQuery] = useState("");
  const [searchText, setSearchText] = useState("");
  const [result, setResult] = useState(null);

  const handleToggle = item => {
    setSelected(
      prev =>
        prev.includes(item)
          ? prev.filter(i => i !== item) // uncheck
          : [...prev, item] // check
    );
  };
  const checkAllBoxesOnSearch = () => {
    const allItems = checkBoxList.map(item => item.name);

    if (selected.length !== allItems.length) {
      setSelected(allItems);
    }
  };

  const searchLocation = async () => {
    checkAllBoxesOnSearch();
    if (!query) return;
    setSearchText(query);

    try {
      const res = await fetch(
        `https://dev.virtualearth.net/REST/v1/Locations?q=${encodeURIComponent(
          query
        )}&key=${process.env.NEXT_PUBLIC_MAP_KEY}`
      );

      const data = await res.json();
      const location = data.resourceSets[0]?.resources[0];

      if (!location) {
        setShowResults(false);
        setShowSorryText(true);
        return;
      }

      const lat = location.point.coordinates[0];
      const lng = location.point.coordinates[1];

      const locationData = {
        name: location.name,
        latitude: lat,
        longitude: lng,
      };
      setLocation(locationData);
      setResult(locationData);
      let storeLsit = await fetchStores({ latitude: lat, longitude: lng });
      setResult(storeLsit);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStoresByCity = async cityName => {
    if (!cityName) return;
    try {
      const res = await fetch(
        `https://dev.virtualearth.net/REST/v1/Locations?q=${encodeURIComponent(
          cityName
        )}&key=${process.env.NEXT_PUBLIC_MAP_KEY}`
      );
      const data = await res.json();
      const loc = data.resourceSets[0]?.resources[0];
      if (!loc) {
        setShowResults(false);
        return;
      }
      const lat = loc.point.coordinates[0];
      const lng = loc.point.coordinates[1];
      const locationData = {
        name: loc.name,
        latitude: lat,
        longitude: lng,
      };
      setLocation(locationData);
      setResult(locationData);
      const storeList = await fetchStores({ latitude: lat, longitude: lng });
      setResult(storeList);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStores = async ({ latitude, longitude }) => {
    if (!latitude || !longitude) {
      console.error("Missing latitude or longitude for fetchStores");
      return null;
    }
    try {
      const url =
        `https://spatial.virtualearth.net/REST/v1/data/f65e8a920e494f1eacd11c17f6cb8f2a/KohlerThailand/KohlerThailand?` +
        `$select=*,__Distance&$top=50&$inlinecount=allpages&$format=json&` +
        `spatialFilter=nearby(${latitude},${longitude},400)&key=${apiKey}`;

      const response = await fetch(url);
      const data = await response.json();
      setShowResults(true);
      return data;
    } catch (error) {
      console.error("fetchStores error:", error);
      setShowResults(false);
      return null;
    }
  };

  useEffect(() => {
    const { __count = 0 } = result?.d || {};
    if (__count > 0) {
      setShowResults(true);
      setShowSorryText(false);
    } else {
      setShowResults(false);
    }
  }, [location]);

  useEffect(() => {
    if (typeof window === "undefined" || !navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async position => {
        try {
          const { latitude: lat, longitude: lon } = position.coords;
          const res = await fetch(
            `https://dev.virtualearth.net/REST/v1/Locations/${lat},${lon}?key=${process.env.NEXT_PUBLIC_MAP_KEY}`
          );
          const data = await res.json();
          const address =
            data?.resourceSets?.[0]?.resources?.[0]?.address || {};
          const country = address.countryRegion || "";
          const countryCode = (address.countryRegionIso2 || "").toLowerCase();
          const city =
            address.locality ||
            address.adminDistrict2 ||
            address.adminDistrict ||
            "";

          const isVietnam =
            country.toLowerCase() === "vietnam" || countryCode === "vn";

          if (isVietnam && city) {
            setQuery(city);
            setSearchText(city);
            fetchStoresByCity(city);
          }
        } catch (err) {
          console.error(err);
        }
      },
      error => {
        console.error(
          "[Geo] error code:",
          error.code,
          "message:",
          error.message
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, []);

  return (
    <>
      <div className={styles.wrapper}>
        <section className="c-koh-store-locator">
          <div className="containers-part">
            <div className="koh-map-form-inner-content">
              <p className="text-center koh-map-main-header w-full">
                {heading}
              </p>
            </div>
            <div className="container-wrapper flex flex-col items-start justify-center gap-[15px] mx-auto px-5">
              <div className="row text-center flex items-center justify-center gap-1 w-full flex-col md:flex-row ">
                <div className="mt-2  w-full md:w-9/12">
                  <div className=" koh-search-text flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300">
                    <input
                      id="koh-search-text"
                      type="text"
                      ref={searchBoxRef}
                      name="search-text"
                      placeholder="City, State/Province or Postal Code"
                      className="block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6 bg-white focus:bg-white autofill:bg-white"
                      defaultValue={query}
                      onChange={e => setQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          searchLocation();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="mt-2 w-full md:w-3/12 cursor-pointer">
                  <button
                    type="button"
                    onClick={searchLocation}
                    className="btn btn-primary koh-store-search-btn cursor-pointer  !font-[HelveticaRoman,_Helvetica,_sans-serif]"
                  >
                    {buttonLabel}
                  </button>
                </div>
              </div>
              <div className="koh-map-check">
                <div className="cursor-pointer form-group flex flex-wrap gap-x-[15px] flex-col md:flex-row gap-5 md:gap-x-[15px]">
                  {checkBoxList.map((item, index) => (
                    <KohlerCheckBox
                      key={index}
                      name={item?.label}
                      value={item?.name}
                      checked={selected.includes(item?.name)}
                      onChange={() => handleToggle(item?.name)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <hr></hr>
          <div className="koh-no-result">
            {showSorryText ? (
              <p className="text-center-info font-extrabold text-3xl text-[#000] text-center mt-10 mb-5 !font-[HelveticaRoman,_Helvetica,_sans-serif]">
                {renderRichText(sorryText)}
              </p>
            ) : null}
          </div>
        </section>
      </div>
      <div
        className={`koh-map-wrapper  relative z-0 mb-5 ${showResults ? "mx-5" : ""}`}
      >
        {showResults ? (
          <SearchResult
            selected={selected}
            searchText={searchText}
            result={result}
            location={location}
            showResults={showResults}
            locale={defaultLocale}
          />
        ) : storeLocationData.showMap ? (
          <MapView location={location} />
        ) : null}
      </div>
    </>
  );
};

StoreLocator.propTypes = {};

export default StoreLocator;
